import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input } from "@cieda/djayma-ui";
import { Truck, Phone, User, Clock, FileText } from "lucide-react";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import type { Delivery, DeliveryDriver, AssignDriverPayload } from "@/lib/admin-api/delivery";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DriverAssignModalProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly delivery: Delivery | null;
  readonly drivers: readonly DeliveryDriver[];
  readonly onAssign: (deliveryId: string, payload: AssignDriverPayload) => Promise<unknown>;
  readonly isAssigning: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function DriverAssignModal({
  open,
  onClose,
  delivery,
  drivers,
  onAssign,
  isAssigning,
}: DriverAssignModalProps) {
  const { t } = useTranslation("admin");
  const [selectedDriverId, setSelectedDriverId] = useState<string>("");
  const [estimatedAt, setEstimatedAt] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  // Reset form when modal opens
  const handleClose = useCallback(() => {
    setSelectedDriverId("");
    setEstimatedAt("");
    setNotes("");
    setError(null);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (!delivery || !selectedDriverId) {
      setError(t("delivery_board.modal.error_select_driver"));
      return;
    }

    setError(null);

    const payload: AssignDriverPayload = {
      driver_id: selectedDriverId,
      ...(estimatedAt ? { estimated_at: new Date(estimatedAt).toISOString() } : {}),
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    };

    try {
      await onAssign(delivery.id, payload);
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t("delivery_board.modal.error_generic"),
      );
    }
  }, [delivery, selectedDriverId, estimatedAt, notes, onAssign, handleClose, t]);

  if (!delivery) return null;

  const selectedDriver = drivers.find((d) => d.id === selectedDriverId);

  return (
    <AdminModal
      open={open}
      onClose={handleClose}
      title={t("delivery_board.modal.title")}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={handleClose} disabled={isAssigning}>
            {t("delivery_board.modal.cancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!selectedDriverId || isAssigning}
            className="gap-1.5"
          >
            {isAssigning ? (
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Truck className="h-4 w-4" />
            )}
            {t("delivery_board.modal.assign_btn")}
          </Button>
        </>
      }
    >
      {/* Delivery summary */}
      <div className="mb-4 rounded-lg border border-stone-100 bg-stone-50 p-3">
        <p className="text-sm font-semibold text-stone-900">
          {delivery.order.public_ref}
        </p>
        <p className="mt-0.5 text-xs text-stone-500">
          {delivery.order.customer_name} — {delivery.order.delivery_address}
        </p>
      </div>

      {/* Driver selection */}
      <div className="space-y-4">
        <div>
          <label
            htmlFor="driver-select"
            className="mb-1.5 block text-sm font-medium text-stone-700"
          >
            <User className="mr-1 inline h-3.5 w-3.5" />
            {t("delivery_board.modal.select_driver")}
          </label>
          <select
            id="driver-select"
            value={selectedDriverId}
            onChange={(e) => setSelectedDriverId(e.target.value)}
            className="h-10 w-full rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-700 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          >
            <option value="">{t("delivery_board.modal.placeholder_driver")}</option>
            {drivers.map((driver) => (
              <option key={driver.id} value={driver.id}>
                {driver.full_name} — {driver.vehicle_type}
              </option>
            ))}
          </select>
        </div>

        {/* Selected driver info */}
        {selectedDriver && (
          <div className="flex items-center gap-3 rounded-lg border border-emerald-100 bg-emerald-50 p-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-100">
              <Truck className="h-4 w-4 text-emerald-700" />
            </div>
            <div>
              <p className="text-sm font-medium text-stone-900">
                {selectedDriver.full_name}
              </p>
              <p className="flex items-center gap-1 text-xs text-stone-500">
                <Phone className="h-3 w-3" />
                {selectedDriver.phone}
                <span className="mx-1 text-stone-300">|</span>
                {selectedDriver.vehicle_type}
              </p>
            </div>
          </div>
        )}

        {/* Estimated delivery time */}
        <div>
          <label
            htmlFor="estimated-at"
            className="mb-1.5 block text-sm font-medium text-stone-700"
          >
            <Clock className="mr-1 inline h-3.5 w-3.5" />
            {t("delivery_board.modal.estimated_time")}
          </label>
          <Input
            id="estimated-at"
            type="datetime-local"
            value={estimatedAt}
            onChange={(e) => setEstimatedAt(e.target.value)}
            className="h-10 w-full text-sm"
          />
        </div>

        {/* Notes */}
        <div>
          <label
            htmlFor="assign-notes"
            className="mb-1.5 block text-sm font-medium text-stone-700"
          >
            <FileText className="mr-1 inline h-3.5 w-3.5" />
            {t("delivery_board.modal.notes")}
          </label>
          <textarea
            id="assign-notes"
            rows={2}
            maxLength={500}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("delivery_board.modal.notes_placeholder")}
            className="w-full rounded-md border border-stone-200 px-3 py-2 text-sm text-stone-700 placeholder:text-stone-400 focus:border-amber-300 focus:outline-none focus:ring-2 focus:ring-amber-100"
          />
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
      </div>
    </AdminModal>
  );
}
